import React, { useEffect, useState, createContext, useContext } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import SITE, { applyTheme } from '../api';
import { usePortal, PORTAL } from '../portalAuth';
import { initAnalytics, trackEvent } from '../analytics';
import { STORE_BASE, STORE_HOME } from '../routeBase';
import VoiceSupportWidget from './VoiceSupportWidget';

const SiteCtx = createContext({ content: {}, menus: {}, categories: [], brands: [] });
export const useSite = () => useContext(SiteCtx);

// /uploads (business licenses, visiting cards, certification scans) now
// requires a valid session token — see backend/middleware/uploadsAuth.js.
// /product-images stays public (product photos, site banners); no token
// needed there, so this only appends one for the /uploads case.
const IMG = p => {
  if (!p) return `/product-images/${p}`;
  const path = p.startsWith('/') ? p : `/product-images/${p}`;
  if (path.startsWith('/uploads/')) {
    const token = localStorage.getItem('sf_customer_token');
    return token ? `${path}${path.includes('?') ? '&' : '?'}token=${token}` : path;
  }
  return path;
};

// Rotating search-bar placeholder — types each example out, holds briefly,
// erases, then moves to the next one, to show off what the AI search bar
// actually understands (model numbers, descriptive text, price, photo search).
const SEARCH_EXAMPLES = [
  'Search 5,000+ models with the model number',
  'Search Black Acetate Eyeglasses',
  'Search Optical Frames under $5',
  'Find a similar model — upload a photo via the camera icon',
];
function useTypewriterPlaceholder(phrases) {
  const [text, setText] = useState('');
  useEffect(() => {
    let phraseIdx = 0, charIdx = 0, deleting = false, timer;
    const TYPE_MS = 45, ERASE_MS = 22, HOLD_MS = 1400, GAP_MS = 350;
    const tick = () => {
      const full = phrases[phraseIdx];
      if (!deleting) {
        charIdx++;
        setText(full.slice(0, charIdx));
        if (charIdx === full.length) { deleting = true; timer = setTimeout(tick, HOLD_MS); return; }
        timer = setTimeout(tick, TYPE_MS);
      } else {
        charIdx--;
        setText(full.slice(0, charIdx));
        if (charIdx === 0) { deleting = false; phraseIdx = (phraseIdx + 1) % phrases.length; timer = setTimeout(tick, GAP_MS); return; }
        timer = setTimeout(tick, ERASE_MS);
      }
    };
    timer = setTimeout(tick, TYPE_MS);
    return () => clearTimeout(timer);
  }, [phrases]);
  return text;
}

// Brands are a separate master list from categories (e.g. brand "Salvador
// Eyewear" vs categories "Salvador Eyewear - Acetate/TR90/Metal") — match by
// exact name or by the category starting with "<brand name> " or "<brand name> -".
export const matchBrandLogo = (categoryName, brands) => {
  if (!categoryName || !brands?.length) return null;
  return brands.find(b => categoryName === b.name || categoryName.startsWith(`${b.name} `) || categoryName.startsWith(`${b.name} -`)) || null;
};

// Map a menu URL to the correct in-app route.
// Direct pages: /, /shop, /about, /contact, /blog → /store + path
// Everything else (policies, other links) → /store/page/<key>
const DIRECT = ['/', '/shop', '/about', '/contact', '/blog', '/world-users', '/tools'];
const resolveLink = (url) => {
  if (!url) return STORE_HOME;
  if (url === '/') return STORE_HOME;
  if (DIRECT.includes(url)) return `${STORE_BASE}${url}`;
  return `${STORE_BASE}/page${url}`;   // /delivery-information → .../page/delivery-information
};

export default function StoreLayout({ children, minimalHeader }) {
  const [site, setSite] = useState({ content: {}, menus: {}, categories: [], brands: [], filters: { gender: [], material: [], frame_type: [] }, razorpayConfigured: false });
  const [ready, setReady] = useState(false);
  const [search, setSearch] = useState('');
  const [searchNoMatch, setSearchNoMatch] = useState(false);
  // Mobile off-canvas nav — the desktop link row (Home/Shop/.../Categories)
  // just wrapped onto 2-3 lines on a phone with no way to collapse it. Below
  // the sf-desktop-nav breakpoint (see <style> below) that row is hidden by
  // CSS and this hamburger + slide-in panel takes over instead.
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [mobileCategoriesOpen, setMobileCategoriesOpen] = useState(false);
  useEffect(() => {
    if (!mobileNavOpen) return;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, [mobileNavOpen]);
  const searchPlaceholder = useTypewriterPlaceholder(SEARCH_EXAMPLES);
  const nav = useNavigate();
  let portal = null;
  try { portal = usePortal(); } catch (e) { portal = null; }

  useEffect(() => {
    Promise.all([SITE.get('/content'), SITE.get('/categories'), SITE.get('/brands'), SITE.get('/filters')])
      .then(([c, cat, br, fl]) => {
        applyTheme(c.data.content || {});
        setSite({
          content: c.data.content || {}, menus: c.data.menus || {}, categories: cat.data.categories || [],
          brands: br.data.brands || [], filters: fl.data || { gender: [], material: [], frame_type: [] },
          razorpayConfigured: !!c.data.razorpay_configured,
        });
        // Analytics & Tracking — bootstraps whatever's configured in
        // Admin > Website > Analytics & Tracking, once, for every public
        // page. See ../analytics.js.
        initAnalytics(c.data.tracking);
      })
      .catch(() => {})
      .finally(() => setReady(true));
  }, []);

  // page_view — the initial load and every client-side route change after
  // it (React Router never reloads the page, so a plain gtag 'config'
  // auto-pageview would only ever see the first URL). Gated on `ready` so
  // this never races ahead of initAnalytics() above — trackEvent() would
  // otherwise silently no-op on the very first page_view of a session.
  const location = useLocation();
  useEffect(() => {
    if (!ready) return;
    trackEvent('page_view', { page_path: location.pathname + location.search, page_title: document.title });
  }, [ready, location.pathname, location.search]);
  useEffect(() => { setMobileNavOpen(false); }, [location.pathname, location.search]);

  const c = site.content;
  const wa = c['brand.whatsapp'] || '919769364624';

  // Natural-language product search — the same box handles a plain model
  // number ("46192"), a descriptive request ("black acetate frames under
  // $5"), or a "similar to model X" lookup. The backend interprets the text
  // and tells us where to send the customer; if nothing in the live
  // catalogue matches, we show a message instead of navigating anywhere.
  const runSearch = q => {
    const client = portal?.customer ? PORTAL : SITE;
    client.get('/ai-search', { params: { q } })
      .then(r => {
        if (r.data.redirect) { setSearchNoMatch(false); nav(r.data.redirect); }
        else setSearchNoMatch(true);
        trackEvent('search', { search_term: q, has_results: !!r.data.redirect });
      })
      .catch(() => setSearchNoMatch(true));
  };

  const doSearch = e => {
    e.preventDefault();
    if (search.trim()) runSearch(search.trim());
  };

  // live search: search as the user types (debounced), no Enter needed
  useEffect(() => {
    const q = search.trim();
    if (q.length < 2) { setSearchNoMatch(false); return; }
    const t = setTimeout(() => runSearch(q), 500);
    return () => clearTimeout(t);
  }, [search]); // eslint-disable-line

  if (!ready) return <div style={{ padding: 60, textAlign: 'center', color: '#888' }}>Loading…</div>;

  return (
    <SiteCtx.Provider value={site}>
      <div className="sf-root" onContextMenu={e => e.preventDefault()}
        style={{ background: 'var(--sf-body-bg, #f7f8fa)', minHeight: '100vh', fontFamily: "'Poppins', 'Segoe UI', sans-serif", color: '#191919' }}>

        {/* Translation engine — always active (even on minimal-header pages
            like entity landing pages) so a googtrans cookie set anywhere
            actually takes effect, not just where the picker UI is visible. */}
        <GoogleTranslateEngine />

        {/* Entity landing pages get a lightweight, identical-everywhere header
            — logo only, no search/cart/mega-nav — since they're a splash page
            that hands off into the full site (which has its own full header)
            once the visitor clicks through. */}
        {minimalHeader ? (
          <header style={{ background: '#fff', borderBottom: '1px solid #eee', padding: '20px 24px', textAlign: 'center' }}>
            <Link to={STORE_HOME} style={{ display: 'inline-flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}>
              {c['brand.logo']
                ? <img src={IMG(c['brand.logo'])} alt={c['brand.name'] || 'Logo'} style={{ height: 46, maxWidth: 200, objectFit: 'contain' }} />
                : <span style={{ fontSize: 26, fontWeight: 700, color: 'var(--sf-primary)', letterSpacing: '-0.5px' }}>{c['brand.name'] || 'Salvador Eyewear'}</span>}
            </Link>
          </header>
        ) : (
        <>
        {/* top announcement bar — the real site's signature light-blue band,
            not a dark strip. Hidden for signed-in distributor customers. */}
        <AnnouncementBar c={c} wa={wa} />

        {/* main header */}
        <header style={{ background: '#fff', borderBottom: '1px solid #eee' }}>
          <div className="sf-header-row" style={{ ...wrap(), padding: '16px 24px', flexWrap: 'wrap', rowGap: 12 }}>
            <button className="sf-hamburger-btn" aria-label="Open menu" onClick={() => setMobileNavOpen(true)}
              style={{ display: 'none', border: '1px solid #eee', background: '#fff', borderRadius: 8, width: 40, height: 40, alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--sf-primary)', flexShrink: 0 }}>
              <i className="ti ti-menu-2" style={{ fontSize: 20 }} />
            </button>
            <Link to={STORE_HOME} style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}>
              {c['brand.logo']
                ? <img src={IMG(c['brand.logo'])} alt={c['brand.name'] || 'Logo'} style={{ height: 40, maxWidth: 180, objectFit: 'contain' }} />
                : (
                  <span style={{ fontSize: 24, fontWeight: 700, color: 'var(--sf-primary)', letterSpacing: '-0.5px' }}>
                    {c['brand.name'] || 'Salvador Eyewear'}
                  </span>
                )}
            </Link>
            <div className="sf-search-form" style={{ position: 'relative' }}>
              <form onSubmit={doSearch} style={{ display: 'flex',
                border: '1.5px solid #eaeaea', borderRadius: 24, overflow: 'hidden', background: '#fafafa' }}>
                <input value={search} onChange={e => { setSearch(e.target.value); setSearchNoMatch(false); }}
                  placeholder={searchPlaceholder} className="sf-search-input"
                  style={{ width: '100%', minWidth: 0, border: 'none', background: 'transparent', padding: '9px 16px', fontSize: 13.5, outline: 'none' }} />
                <Link to={`${STORE_BASE}/visual-search`} title="Search by photo" onClick={e => e.stopPropagation()}
                  style={{ flex: 'none', display: 'flex', alignItems: 'center', padding: '0 12px', color: 'var(--sf-primary)', borderLeft: '1px solid #eaeaea' }}>
                  <i className="ti ti-camera" />
                </Link>
                <button type="submit" style={{ flex: 'none', border: 'none', background: 'var(--sf-primary)', color: '#fff', padding: '0 18px', cursor: 'pointer' }}>
                  <i className="ti ti-search" />
                </button>
              </form>
              {searchNoMatch && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 6, background: '#fff',
                  border: '1px solid #eee', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', padding: '14px 16px', zIndex: 200 }}>
                  <div style={{ fontSize: 13, color: '#444', lineHeight: 1.6, marginBottom: 8 }}>
                    We couldn't find a match for that search in our current catalogue. Please get in touch with our head office with your specific requirements — our team will be glad to help you find the right frame.
                  </div>
                  <Link to={`${STORE_BASE}/contact`} style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--sf-accent)', textDecoration: 'none' }} onClick={() => setSearchNoMatch(false)}>
                    Contact Us <i className="ti ti-arrow-right" />
                  </Link>
                </div>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <TranslateWidget />
              <AccountArea />
            </div>
          </div>

          {/* nav — desktop link row; hidden below sf-desktop-nav's breakpoint
              in favour of the hamburger + off-canvas drawer rendered below */}
          <nav className="sf-desktop-nav" style={{ borderTop: '1px solid #f0f0f0' }}>
            <div style={{ ...wrap(), padding: '0 24px', justifyContent: 'flex-start', gap: 4, flexWrap: 'wrap' }}>
              {(site.menus.header || []).map(m => m.url === '/shop' ? (
                // Shop still opens the page directly on click — the dropdown is
                // purely a hover-reveal extra, not a replacement for the link.
                <div key={m.label} style={{ position: 'relative' }} className="sf-dropdown">
                  <Link to={resolveLink(m.url)} style={navLink}>{m.label} <i className="ti ti-chevron-down" style={{ fontSize: 13 }} /></Link>
                  <div className="sf-dropdown-menu" style={{ ...dropdown, minWidth: 760, maxWidth: 'calc(100vw - 48px)', padding: 18 }}>
                    <div style={{ display: 'flex', gap: 26 }}>
                      <DropCol title="Shop by Gender" items={(site.filters.gender || []).map(g => ({ label: g, to: `${STORE_BASE}/shop?gender=${encodeURIComponent(g)}` }))} />
                      <DropCol title="Shop by Material" items={(site.filters.material || []).map(m2 => ({ label: m2, to: `${STORE_BASE}/shop?material=${encodeURIComponent(m2)}` }))} />
                      <DropCol title="Shop by Frame Type" items={(site.filters.frame_type || []).map(f => ({ label: f, to: `${STORE_BASE}/shop?frame_type=${encodeURIComponent(f)}` }))} />
                      <DropCol title="Shop by Shape" items={(site.filters.shape || []).map(s => ({ label: s, to: `${STORE_BASE}/shop?shape=${encodeURIComponent(s)}` }))} />
                      <div style={{ minWidth: 150 }}>
                        <div style={dropColTitle}>Top Brands</div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                          {site.brands.slice(0, 6).map(b => (
                            <Link key={b.id} to={`${STORE_BASE}/shop?brand=${encodeURIComponent(b.name)}`} title={b.name} className="sf-brand-tile"
                              style={{ height: 44, border: '1px solid #eee', borderRadius: 6, background: '#fafafa', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 6 }}>
                              {b.logo ? <img src={IMG(b.logo)} alt={b.name} loading="lazy" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} /> : <span style={{ fontSize: 9.5, textAlign: 'center' }}>{b.name}</span>}
                            </Link>
                          ))}
                        </div>
                      </div>
                      <DropBanner image={c['nav.shop_banner_image']} link={c['nav.shop_banner_link']} width={192} height={280} />
                    </div>
                  </div>
                </div>
              ) : (
                <Link key={m.label} to={resolveLink(m.url)} style={navLink}>{m.label}</Link>
              ))}
              <div style={{ position: 'relative' }} className="sf-dropdown">
                <span style={navLink}>Categories <i className="ti ti-chevron-down" style={{ fontSize: 13 }} /></span>
                <div className="sf-dropdown-menu" style={{ ...dropdown, maxWidth: 'calc(100vw - 48px)', padding: 14 }}>
                  <div style={{ display: 'flex', gap: 20 }}>
                    <div style={{ minWidth: 230, maxHeight: 380, overflowY: 'auto' }}>
                      {site.categories.slice(0, 12).map(cat => {
                        const brand = matchBrandLogo(cat.name, site.brands);
                        return (
                          <Link key={cat.name} to={`${STORE_BASE}/shop?category=${encodeURIComponent(cat.name)}`} style={{ ...dropItem, display: 'flex', alignItems: 'center', gap: 8 }}>
                            {brand?.logo && <img src={IMG(brand.logo)} alt="" loading="lazy" style={{ width: 18, height: 18, objectFit: 'contain', flexShrink: 0 }} />}
                            <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{cat.name}</span>
                            <span style={{ color: '#aaa', fontSize: 11, flexShrink: 0 }}>{cat.count}</span>
                          </Link>
                        );
                      })}
                    </div>
                    <DropBanner image={c['nav.categories_banner_image']} link={c['nav.categories_banner_link']} width={208} height={340} />
                  </div>
                </div>
              </div>
            </div>
          </nav>
        </header>

        {/* Mobile off-canvas nav — same links as the desktop row above, one
            per line, plus an expandable Categories list (mirrors the
            FilterGroup accordion pattern used on the Shop page). */}
        {mobileNavOpen && (
          <>
            <div className="sf-mobile-nav-backdrop" onClick={() => setMobileNavOpen(false)} />
            <div className="sf-mobile-nav-drawer" role="dialog" aria-label="Site menu">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '1px solid #eee' }}>
                <span style={{ fontWeight: 700, fontSize: 15 }}>Menu</span>
                <button aria-label="Close menu" onClick={() => setMobileNavOpen(false)}
                  style={{ border: 'none', background: 'none', fontSize: 22, lineHeight: 1, cursor: 'pointer', color: '#666', padding: 4 }}>
                  <i className="ti ti-x" />
                </button>
              </div>
              <div style={{ overflowY: 'auto', flex: 1, padding: '6px 6px 24px' }}>
                {(site.menus.header || []).map(m => (
                  <Link key={m.label} to={resolveLink(m.url)} onClick={() => setMobileNavOpen(false)} style={mobileNavLink}>{m.label}</Link>
                ))}
                <button onClick={() => setMobileCategoriesOpen(o => !o)} style={{ ...mobileNavLink, width: '100%', border: 'none', background: 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', font: 'inherit' }}>
                  Categories
                  <i className={`ti ti-chevron-${mobileCategoriesOpen ? 'up' : 'down'}`} style={{ fontSize: 15, color: '#999' }} />
                </button>
                {mobileCategoriesOpen && (
                  <div style={{ paddingLeft: 14, paddingBottom: 8 }}>
                    {site.categories.slice(0, 20).map(cat => (
                      <Link key={cat.name} to={`${STORE_BASE}/shop?category=${encodeURIComponent(cat.name)}`} onClick={() => setMobileNavOpen(false)}
                        style={{ display: 'flex', justifyContent: 'space-between', padding: '9px 10px', fontSize: 13, color: '#555', textDecoration: 'none' }}>
                        <span>{cat.name}</span>
                        <span style={{ color: '#aaa' }}>{cat.count}</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
        </>
        )}

        <main>{children}</main>

        <WhatsAppWidget wa={wa} greeting={c['whatsapp.widget_greeting']} />
        <VoiceSupportWidget />
        <BackToTopButton />

        <NewsletterRow c={c} />

        {/* footer — the real site's footer is light gray with a light-blue
            "be in touch" band, not a dark fill */}
        <footer style={{ background: '#f7f8fa', color: '#666', marginTop: 50, padding: '40px 0 0', borderTop: '1px solid #eee' }}>
          <div style={{ ...wrap(), padding: '0 24px', alignItems: 'flex-start', flexWrap: 'wrap', gap: 30 }}>
            <div style={{ flex: '1 1 240px' }}>
              {c['footer.brand_logo']
                ? <img src={IMG(c['footer.brand_logo'])} alt={c['brand.name']} loading="lazy" style={{ maxWidth: 160, maxHeight: 64, objectFit: 'contain', display: 'block', marginBottom: 10 }} />
                : <div style={{ fontSize: 20, fontWeight: 700, color: '#191919', marginBottom: 6 }}>{c['brand.name']}</div>}
              {c['brand.tagline'] && <p style={{ fontSize: 12.5, lineHeight: 1.6, color: '#888', marginBottom: 10 }}>{c['brand.tagline']}</p>}
              <SocialIcons c={c} />
            </div>
            <FooterCol title="Other Links" items={site.menus.footer_links} />
            <div style={{ flex: '1 1 160px' }}>
              <div style={ftitle}>Policies</div>
              {(site.menus.footer_policies || []).map(i => (
                <Link key={i.label} to={resolveLink(i.url)} style={{ display: 'block', color: '#777', fontSize: 12.5, textDecoration: 'none', padding: '3px 0' }}>{i.label}</Link>
              ))}
              <Link to={`${STORE_BASE}/mobile-app`} style={{ ...ftitle, display: 'block', textDecoration: 'none', marginTop: 20 }}>Download Our App</Link>
              <GooglePlayBadge c={c} />
            </div>
            <div style={{ flex: '1 1 220px' }}>
              <div style={ftitle}>Contact Us</div>
              <p style={{ fontSize: 12.5, lineHeight: 1.7, color: '#777' }}>{c['brand.address']}</p>
              <p style={{ fontSize: 12.5, marginTop: 8, color: '#777' }}>
                {c['brand.phone'] && (
                  <a href={`tel:${c['brand.phone'].replace(/\s+/g, '')}`} style={{ color: 'inherit', textDecoration: 'none' }}
                    onClick={() => trackEvent('phone_click', { location: 'footer' })}>
                    <i className="ti ti-phone" /> {c['brand.phone']}
                  </a>
                )}<br />
                <i className="ti ti-mail" /> {c['brand.email']}<br />
                <i className="ti ti-clock" /> {c['brand.hours']}
              </p>
            </div>
          </div>

          <FooterBottomBar c={c} />
        </footer>
      </div>

      <style>{`
        .sf-dropdown-menu { display: none; }
        .sf-dropdown:hover .sf-dropdown-menu { display: block; }

        /* Global link hover — every plain link's text picks up the site's
           mouse-over colour (admin-editable in Website Editor > Colours >
           "Mouse-over colour"). Buttons/icons/badges that need to keep their
           own hover colour (white text on a solid dark button, etc.) opt out
           with .sf-hover-keep, which wins on specificity even though both
           rules are !important. */
        a:hover, a:focus-visible { color: var(--sf-hover, #85cde9) !important; opacity: 1; transition: color .15s ease; }
        .sf-hover-keep:hover, .sf-hover-keep:focus-visible { color: var(--sf-hover-keep, inherit) !important; opacity: 0.8; }

        .sf-search-form { flex: 1 1 260px; max-width: 460px; margin: 0 30px; }
        /* The rotating example placeholder can run longer than what's typed
           ("Find a similar model — upload a photo via the camera icon…"),
           so it gets its own smaller size — typed text stays at 13.5px. */
        .sf-search-input::placeholder { font-size: 12px; opacity: 0.65; }
        @media (max-width: 700px) {
          .sf-search-form { flex: 1 1 100%; max-width: none; margin: 0; order: 3; }
        }
        #goog-gt-tt, .goog-te-balloon-frame { display: none !important; }
        .goog-text-highlight { background: none !important; box-shadow: none !important; }

        /* Site-wide button/icon hover — one shared pattern everywhere it's used:
           an accent-coloured outline that fills solid, in the site's mouse-over
           colour, on hover. Callers keep their own padding/border-radius/
           font-size inline; only color, background and the hover transition
           live here, since inline styles can't express :hover. */
        .sf-btn-outline { color: var(--sf-accent, #85cde9); background: transparent; transition: background .2s ease, color .2s ease; }
        .sf-btn-outline:hover, .sf-btn-outline:focus-visible { background: var(--sf-hover, #85cde9) !important; color: #fff !important; opacity: 1; }
        /* Persistently-selected state for toggle buttons (grid columns, text
           size, active filters) — same solid fill as the hover state, just
           "stuck on" instead of transient. */
        .sf-btn-outline.active { background: var(--sf-accent, #85cde9); color: #fff; }
        .sf-social-icon { background: #eef1f4; color: #555; transition: background .2s ease, color .2s ease; }
        .sf-social-icon:hover, .sf-social-icon:focus-visible { background: var(--sf-hover, #85cde9) !important; color: #fff !important; opacity: 1; }

        /* Rich-text content (blog posts, static pages, landing-page intros) —
           all rendered via dangerouslySetInnerHTML straight from the Quill
           editor's saved HTML. The site-wide "* { margin:0 }" reset in
           index.css (needed everywhere else so cards/buttons/layout divs
           start from a clean zero) also zeroes out every h1-h6/p/ul/li's
           default spacing, so headings, bold text and paragraph breaks were
           all present in the DOM but visually flattened into one undifferen-
           tiated block. This class restores just enough spacing/hierarchy —
           callers keep their own inline font-size/line-height/color. */
        .sf-richtext h1, .sf-richtext h2, .sf-richtext h3, .sf-richtext h4 { font-weight: 700; line-height: 1.3; margin: 1em 0 0.5em; }
        .sf-richtext h1:first-child, .sf-richtext h2:first-child, .sf-richtext h3:first-child, .sf-richtext h4:first-child { margin-top: 0; }
        .sf-richtext h1 { font-size: 1.8em; }
        .sf-richtext h2 { font-size: 1.5em; }
        .sf-richtext h3 { font-size: 1.25em; }
        .sf-richtext h4 { font-size: 1.1em; }
        .sf-richtext p { margin: 0 0 1em; }
        .sf-richtext p:last-child { margin-bottom: 0; }
        .sf-richtext ul, .sf-richtext ol { margin: 0 0 1em; padding-left: 1.4em; }
        .sf-richtext li { margin-bottom: 0.35em; }
        .sf-richtext blockquote { margin: 1em 0; padding: 4px 0 4px 16px; border-left: 3px solid var(--sf-accent, #85cde9); color: #666; }
        .sf-richtext img { max-width: 100%; height: auto; border-radius: 8px; }
        .sf-richtext a { color: var(--sf-accent, inherit); }
        .sf-richtext strong, .sf-richtext b { font-weight: 700; }
        /* Product-card colour swatch strip — scrolls horizontally when a
           model has more colours than fit in the card width. */
        .sf-swatch-row { scrollbar-width: thin; }
        .sf-swatch-row::-webkit-scrollbar { height: 4px; }
        .sf-swatch-row::-webkit-scrollbar-thumb { background: #ddd; border-radius: 2px; }

        /* "Other Recommendations" related-products grid — fixed 5 columns so
           a full 10-item set reads as two even 5+5 rows on a computer screen
           (the caller trims the item count to a multiple of 5 to match).
           Steps down on narrower screens instead of squeezing 5 tiny columns. */
        .sf-related-grid { grid-template-columns: repeat(5, 1fr); }
        @media (max-width: 1100px) { .sf-related-grid { grid-template-columns: repeat(3, 1fr); } }
        @media (max-width: 700px) { .sf-related-grid { grid-template-columns: repeat(2, 1fr); } }

        /* Clickable brand-logo tiles (About page, Shop dropdown) — link
           straight to that brand's filtered shop listing, everywhere they
           appear, automatically covering any brand added later. */
        .sf-brand-tile { transition: border-color .15s ease, box-shadow .15s ease; }
        .sf-brand-tile:hover, .sf-brand-tile:focus-visible { border-color: var(--sf-hover, #85cde9) !important; box-shadow: 0 2px 10px rgba(0,0,0,0.08); opacity: 1; }

        /* Deter casual copying — text selection, image dragging/saving, and
           the right-click context menu are disabled across the storefront.
           This is a deterrent, not real protection: it only stops a normal
           right-click/drag/select; browser dev tools, view-source, or
           disabling JavaScript bypass it entirely, as they do on any site. */
        .sf-root, .sf-root *:not(input):not(textarea) {
          user-select: none; -webkit-user-select: none; -moz-user-select: none; -ms-user-select: none;
        }
        .sf-root input, .sf-root textarea { user-select: text; -webkit-user-select: text; }
        .sf-root img { -webkit-user-drag: none; user-drag: none; pointer-events: auto; }

        /* Product page hover-to-zoom — the image scales up on hover; its
           transform-origin is set imperatively (Product.js) to track the
           cursor, so the zoom follows the mouse like a standard PDP zoom. */
        .sf-zoom-img { transition: transform .12s ease-out; }
        .sf-zoom-box:hover .sf-zoom-img { transform: scale(2.2); }

        /* "From The Journal" blog cards (Pure Gold Frames, Rx Swimming
           Goggles, Stock & Rx Lenses) — a small lift + shadow so a fully
           clickable card actually looks clickable on hover, matching the
           lift used on admin stat/KPI cards. */
        .sf-journal-card { transition: transform .18s ease, box-shadow .18s ease; }
        .sf-journal-card:hover, .sf-journal-card:focus-visible { transform: translateY(-3px); box-shadow: 0 10px 24px rgba(0,0,0,0.12); }

        /* Shop FAQ "Related Reading" links — an explicit underline on hover
           in addition to the global link-colour rule above, so the cue isn't
           colour alone. */
        .sf-related-reading a:hover, .sf-related-reading a:focus-visible { text-decoration: underline; }

        /* Optional, subtle entrance for the top-of-page hero banner — a
           quiet fade + rise on first paint, not a scroll-triggered effect.
           Off entirely for anyone who's asked the OS for reduced motion. */
        @media (prefers-reduced-motion: no-preference) {
          @keyframes sf-fade-in { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
          .sf-fade-in { animation: sf-fade-in .5s ease-out both; }
        }

        /* Mobile nav — below this width the desktop link row (which just
           wrapped onto 2-3 lines with no way to collapse) is swapped for a
           hamburger button + off-canvas drawer instead. */
        @media (max-width: 900px) {
          .sf-desktop-nav { display: none !important; }
          .sf-hamburger-btn { display: flex !important; }
        }
        .sf-mobile-nav-backdrop {
          position: fixed; inset: 0; background: rgba(0,0,0,0.4); z-index: 999;
        }
        .sf-mobile-nav-drawer {
          position: fixed; top: 0; left: 0; bottom: 0; width: 82vw; max-width: 340px;
          background: #fff; z-index: 1000; box-shadow: 4px 0 24px rgba(0,0,0,0.15);
          display: flex; flex-direction: column;
          animation: sf-slide-in .2s ease-out both;
        }
        @keyframes sf-slide-in { from { transform: translateX(-100%); } to { transform: translateX(0); } }
        @media (prefers-reduced-motion: reduce) { .sf-mobile-nav-drawer { animation: none; } }

        /* Shop page — filter sidebar + product grid. Below 900px the fixed
           220px sidebar (aside.sf-shop-filters-desktop) is replaced by a
           "Filters" button (.sf-shop-filters-btn) opening a bottom-sheet
           drawer instead — the sidebar sitting alongside the grid at a fixed
           width was what pushed the whole page wider than the viewport on a
           phone, forcing the page itself to scroll sideways. */
        @media (max-width: 900px) {
          .sf-shop-filters-desktop { display: none !important; }
          .sf-shop-filters-btn { display: inline-flex !important; }
        }
        .sf-shop-filters-drawer {
          position: fixed; left: 0; right: 0; bottom: 0; max-height: 82vh;
          background: #fff; z-index: 1000; border-radius: 16px 16px 0 0;
          box-shadow: 0 -4px 24px rgba(0,0,0,0.15);
          display: flex; flex-direction: column;
          animation: sf-slide-up .2s ease-out both;
        }
        @keyframes sf-slide-up { from { transform: translateY(100%); } to { transform: translateY(0); } }
        @media (prefers-reduced-motion: reduce) { .sf-shop-filters-drawer { animation: none; } }
        /* Grid always forces 2 columns on a phone regardless of a 3/4-per-row
           preference saved from a previous desktop visit (localStorage is
           shared across devices on the same browser) — 3-4 columns of a
           234-model catalogue were unreadably cramped on a 6" screen. The
           per-row selector itself is hidden at the same breakpoint since
           there's nothing left for it to control. */
        @media (max-width: 700px) {
          .sf-shop-grid { grid-template-columns: repeat(2, 1fr) !important; }
          .sf-shop-gridsize { display: none !important; }
        }
      `}</style>
    </SiteCtx.Provider>
  );
}

function AnnouncementBar({ c, wa }) {
  let portal = null;
  try { portal = usePortal(); } catch (e) { portal = null; }
  if (portal?.customer) return null; // signed-in customers don't need the register/login prompt

  return (
    <div style={{ background: 'var(--sf-accent, #85cde9)', color: '#191919', fontSize: 12.5, padding: '7px 0' }}>
      <div style={wrap()}>
        <span style={{ fontWeight: 600 }}>
          {c['announcement.text'] || 'Please register or login to view wholesale prices'}
        </span>
        <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
          <a href={`https://api.whatsapp.com/send?phone=${wa}`} className="sf-hover-keep" style={{ ...topLink, color: '#191919', '--sf-hover-keep': '#191919' }}
            onClick={() => trackEvent('whatsapp_click', { location: 'announcement_bar' })}>
            <i className="ti ti-brand-whatsapp" /> {c['brand.phone'] || '+91 9769364624'}
          </a>
          <Link to={`${STORE_BASE}/register`} className="sf-hover-keep" style={{ ...topLink, color: '#191919', fontWeight: 700, textDecoration: 'underline', '--sf-hover-keep': '#191919' }}>
            Register
          </Link>
          <Link to={`${STORE_BASE}/login`} className="sf-hover-keep" style={{ ...topLink, color: '#191919', fontWeight: 700, textDecoration: 'underline', '--sf-hover-keep': '#191919' }}>
            Sign in
          </Link>
        </div>
      </div>
    </div>
  );
}

// Floating "chat with us" button, present on every storefront page. Opens
// the real WhatsApp Business number already used elsewhere on the site
// (same number as the header contact link and the price-gate "Contact us"
// link on Product.js) — replies land in Admin > WhatsApp Inbox via the
// Meta webhook (routes/whatsappWebhook.js), no separate chat account.
// Floating "scroll to top" button — stacked directly above the WhatsApp
// bubble so the two never overlap. Only appears once the visitor has
// scrolled down a page's worth, which is exactly where it's useful: long
// autoscrolling pages (category/shop grids, the landing pages) where
// getting back to the header otherwise means a long manual scroll.
function BackToTopButton() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 500);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  if (!visible) return null;
  return (
    <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      title="Back to top" aria-label="Back to top"
      style={{
        position: 'fixed', right: 22, bottom: 90, zIndex: 999,
        width: 44, height: 44, borderRadius: '50%', border: 'none',
        background: 'var(--sf-primary, #191919)', color: 'var(--sf-accent, #85cde9)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: '0 4px 16px rgba(0,0,0,0.25)', cursor: 'pointer',
        transition: 'transform .15s ease',
      }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.08)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}>
      <i className="ti ti-chevron-up" style={{ fontSize: 22 }} />
    </button>
  );
}

function WhatsAppWidget({ wa, greeting }) {
  const text = encodeURIComponent(greeting || "Hi, I'd like to know more about your products.");
  return (
    <a href={`https://api.whatsapp.com/send?phone=${wa}&text=${text}`} target="_blank" rel="noopener noreferrer"
      title="Chat with us on WhatsApp"
      onClick={() => trackEvent('whatsapp_click', { location: 'floating_widget' })}
      style={{
        position: 'fixed', right: 22, bottom: 22, zIndex: 999,
        width: 56, height: 56, borderRadius: '50%', background: '#25D366',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: '0 4px 16px rgba(0,0,0,0.25)', textDecoration: 'none',
        transition: 'transform .15s ease',
      }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.08)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}>
      <i className="ti ti-brand-whatsapp" style={{ fontSize: 30, color: '#fff' }} />
    </a>
  );
}

// Site-wide newsletter signup row, shown just above the footer on every
// page. Dark section on the site's primary colour with a solid accent
// button, matching the announcement bar / register-CTA band's existing use
// of --sf-accent as the "call to action" colour elsewhere on the site.
function NewsletterRow({ c }) {
  const [email, setEmail] = useState('');
  const [hp, setHp] = useState(''); // honeypot — real visitors never fill this in
  const [status, setStatus] = useState('idle'); // idle | busy | done | error
  const [err, setErr] = useState('');

  if (c['newsletter.enabled'] === '0') return null;

  const submit = async e => {
    e.preventDefault();
    setStatus('busy'); setErr('');
    try {
      await SITE.post('/newsletter-subscribe', { email, hp });
      setStatus('done'); setEmail('');
      trackEvent('newsletter_signup', { location: 'footer_row' });
    } catch (e2) {
      setErr(e2.response?.data?.error || 'Could not subscribe'); setStatus('error');
    }
  };

  return (
    <section style={{ background: 'var(--sf-primary, #191919)', color: '#fff' }}>
      <div style={{ ...wrap(), padding: '30px 24px', flexWrap: 'wrap', gap: 18 }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700, letterSpacing: 0.3 }}>{c['newsletter.heading'] || 'Subscribe Us Now'}</div>
          {c['newsletter.subtext'] && <div style={{ fontSize: 13, color: '#bbb', marginTop: 4 }}>{c['newsletter.subtext']}</div>}
        </div>
        {status === 'done' ? (
          <div style={{ fontSize: 13.5, color: 'var(--sf-accent, #85cde9)', fontWeight: 600 }}>
            <i className="ti ti-circle-check" /> Thanks for subscribing!
          </div>
        ) : (
          <form onSubmit={submit} style={{ display: 'flex', gap: 0, flex: '1 1 380px', maxWidth: 460 }}>
            <input type="email" required value={email} onChange={e => setEmail(e.target.value)}
              placeholder={c['newsletter.placeholder'] || 'Your email address here'}
              style={{ flex: 1, minWidth: 0, border: 'none', padding: '12px 16px', fontSize: 13.5, outline: 'none' }} />
            {/* honeypot — hidden from real visitors, invisible bots often fill every field */}
            <input type="text" value={hp} onChange={e => setHp(e.target.value)} tabIndex={-1} autoComplete="off"
              style={{ position: 'absolute', left: -9999, width: 1, height: 1, opacity: 0 }} aria-hidden="true" />
            <button type="submit" disabled={status === 'busy'} style={{
              flexShrink: 0, border: 'none', background: 'var(--sf-accent, #85cde9)', color: '#fff',
              padding: '12px 26px', fontSize: 13, fontWeight: 700, letterSpacing: 0.5, cursor: 'pointer',
            }}>
              {status === 'busy' ? '…' : (c['newsletter.button_label'] || 'Subscribe').toUpperCase()}
            </button>
          </form>
        )}
      </div>
      {status === 'error' && <div style={{ ...wrap(), padding: '0 24px 14px', fontSize: 12.5, color: '#f87171' }}>{err}</div>}
    </section>
  );
}

function DropCol({ title, items }) {
  return (
    <div style={{ minWidth: 140 }}>
      <div style={dropColTitle}>{title}</div>
      {items.length
        ? items.map(it => <Link key={it.label} to={it.to} style={{ ...dropItem, padding: '6px 0', whiteSpace: 'nowrap' }}>{it.label}</Link>)
        : <span style={{ fontSize: 12, color: '#bbb' }}>—</span>}
    </div>
  );
}

// Shared by the Categories and Shop nav dropdowns — admin-editable from
// Website Editor → Header Menu Banners. Renders nothing if no image is set.
function DropBanner({ image, link, width, height }) {
  if (!image) return null;
  const img = <img src={IMG(image)} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8, display: 'block' }} />;
  return (
    <div style={{ width, height, flexShrink: 0 }}>
      {link ? <Link to={link} style={{ display: 'block', width: '100%', height: '100%' }}>{img}</Link> : img}
    </div>
  );
}

function AccountArea() {
  let portal = null;
  try { portal = usePortal(); } catch (e) { portal = null; }
  const customer = portal?.customer;
  const cartCount = portal?.cart?.count || 0;
  const wishCount = portal?.wishlist?.count || 0;
  const compareCount = portal?.compare?.count || 0;
  return (
    <div style={{ display: 'flex', gap: 22, alignItems: 'center', fontSize: 13 }}>
      {customer ? (
        <div className="sf-dropdown" style={{ position: 'relative' }}>
          <span style={{ ...iconLink, cursor: 'pointer' }}><i className="ti ti-user-check" style={{ fontSize: 19, color: 'var(--sf-accent)' }} /><span>{(customer.company || 'Account').slice(0, 12)}</span></span>
          <div className="sf-dropdown-menu" style={{ position: 'absolute', top: '100%', right: 0, background: '#fff', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', borderRadius: 8, padding: 8, minWidth: 160, zIndex: 100 }}>
            <Link to={`${STORE_BASE}/dashboard`} style={{ display: 'block', padding: '8px 12px', fontSize: 13, color: '#333', textDecoration: 'none' }}>Dashboard</Link>
            <Link to={`${STORE_BASE}/orders`} style={{ display: 'block', padding: '8px 12px', fontSize: 13, color: '#333', textDecoration: 'none' }}>My Orders</Link>
            <Link to={`${STORE_BASE}/bulk-order`} style={{ display: 'block', padding: '8px 12px', fontSize: 13, color: '#333', textDecoration: 'none' }}>Bulk Order</Link>
            <Link to={`${STORE_BASE}/support-tickets`} style={{ display: 'block', padding: '8px 12px', fontSize: 13, color: '#333', textDecoration: 'none' }}>Support Tickets</Link>
            <Link to={`${STORE_BASE}/tools/patient-recall`} style={{ display: 'block', padding: '8px 12px', fontSize: 13, color: '#333', textDecoration: 'none' }}>Patient Recall</Link>
            <Link to={`${STORE_BASE}/profile`} style={{ display: 'block', padding: '8px 12px', fontSize: 13, color: '#333', textDecoration: 'none' }}>My Profile</Link>
            <button onClick={() => { portal.logout(); window.location.href = STORE_HOME; }} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px', fontSize: 13, color: '#c0392b', background: 'none', border: 'none', cursor: 'pointer' }}>Sign out</button>
          </div>
        </div>
      ) : (
        <Link to={`${STORE_BASE}/login`} style={iconLink}><i className="ti ti-user" style={{ fontSize: 19 }} /><span>Account</span></Link>
      )}
      <Link to={`${STORE_BASE}/wishlist`} style={{ ...iconLink, position: 'relative' }}>
        <i className="ti ti-heart" style={{ fontSize: 19 }} />
        <span>Wish List</span>
        {wishCount > 0 && <span style={badgeStyle}>{wishCount}</span>}
      </Link>
      <Link to={`${STORE_BASE}/compare`} style={{ ...iconLink, position: 'relative' }}>
        <i className="ti ti-arrows-right-left" style={{ fontSize: 19 }} />
        <span>Compare</span>
        {compareCount > 0 && <span style={badgeStyle}>{compareCount}</span>}
      </Link>
      <Link to={`${STORE_BASE}/cart`} style={{ ...iconLink, position: 'relative' }}>
        <i className="ti ti-shopping-cart" style={{ fontSize: 19 }} />
        <span>Cart</span>
        {cartCount > 0 && <span style={badgeStyle}>{cartCount}</span>}
      </Link>
    </div>
  );
}
const badgeStyle = { position: 'absolute', top: -6, right: -10, background: 'var(--sf-accent)', color: '#191919', borderRadius: 10, fontSize: 10, padding: '0 5px', fontWeight: 700 };

// Free Google "Website Translator" widget — no API key, no billing. Google's
// own dropdown is barebones and hard to restyle (it lives inside its own
// iframe/select with almost no CSS hooks), so we drive it from a custom
// button + menu instead: the real Google <select> is mounted off-screen,
// and picking a language in our menu sets that select's value and fires a
// Google's SIMPLE layout renders a "Select Language" link that opens a
// cross-origin popup iframe (translate.google.com) — there is no scriptable
// <select> in the main document for it, so it can't be driven the classic
// "goog-te-combo" way. Instead we drive it the way the widget itself
// persists a choice across page loads: the googtrans cookie. Setting it and
// reloading is exactly what clicking a language in Google's own dropdown
// does under the hood. No includedLanguages restriction — this is the full
// list of ~130 languages Google Translate's website widget supports (all
// public info, not fetched from Google — there's no endpoint that lists them).
const GOOGLE_TRANSLATE_LANGUAGES = [
  ['af','Afrikaans'],['sq','Albanian'],['am','Amharic'],['ar','Arabic'],['hy','Armenian'],['as','Assamese'],
  ['ay','Aymara'],['az','Azerbaijani'],['bm','Bambara'],['eu','Basque'],['be','Belarusian'],['bn','Bengali'],
  ['bho','Bhojpuri'],['bs','Bosnian'],['bg','Bulgarian'],['ca','Catalan'],['ceb','Cebuano'],['ny','Chichewa'],
  ['zh-CN','Chinese (Simplified)'],['zh-TW','Chinese (Traditional)'],['co','Corsican'],['hr','Croatian'],
  ['cs','Czech'],['da','Danish'],['dv','Dhivehi'],['doi','Dogri'],['nl','Dutch'],['en','English'],['eo','Esperanto'],
  ['et','Estonian'],['ee','Ewe'],['tl','Filipino'],['fi','Finnish'],['fr','French'],['fy','Frisian'],['gl','Galician'],
  ['ka','Georgian'],['de','German'],['el','Greek'],['gn','Guarani'],['gu','Gujarati'],['ht','Haitian Creole'],
  ['ha','Hausa'],['haw','Hawaiian'],['he','Hebrew'],['hi','Hindi'],['hmn','Hmong'],['hu','Hungarian'],['is','Icelandic'],
  ['ig','Igbo'],['ilo','Ilocano'],['id','Indonesian'],['ga','Irish'],['it','Italian'],['ja','Japanese'],['jw','Javanese'],
  ['kn','Kannada'],['kk','Kazakh'],['km','Khmer'],['rw','Kinyarwanda'],['gom','Konkani'],['ko','Korean'],['kri','Krio'],
  ['ku','Kurdish'],['ckb','Kurdish (Sorani)'],['ky','Kyrgyz'],['lo','Lao'],['la','Latin'],['lv','Latvian'],['ln','Lingala'],
  ['lt','Lithuanian'],['lg','Luganda'],['lb','Luxembourgish'],['mk','Macedonian'],['mai','Maithili'],['mg','Malagasy'],
  ['ms','Malay'],['ml','Malayalam'],['mt','Maltese'],['mi','Maori'],['mr','Marathi'],['mni-Mtei','Meiteilon (Manipuri)'],
  ['lus','Mizo'],['mn','Mongolian'],['my','Myanmar (Burmese)'],['ne','Nepali'],['no','Norwegian'],['or','Odia (Oriya)'],
  ['om','Oromo'],['ps','Pashto'],['fa','Persian'],['pl','Polish'],['pt','Portuguese'],['pa','Punjabi'],['qu','Quechua'],
  ['ro','Romanian'],['ru','Russian'],['sm','Samoan'],['sa','Sanskrit'],['gd','Scots Gaelic'],['nso','Sepedi'],
  ['sr','Serbian'],['st','Sesotho'],['sn','Shona'],['sd','Sindhi'],['si','Sinhala'],['sk','Slovak'],['sl','Slovenian'],
  ['so','Somali'],['es','Spanish'],['su','Sundanese'],['sw','Swahili'],['sv','Swedish'],['tg','Tajik'],['ta','Tamil'],
  ['tt','Tatar'],['te','Telugu'],['th','Thai'],['ti','Tigrinya'],['ts','Tsonga'],['tr','Turkish'],['tk','Turkmen'],
  ['ak','Twi'],['uk','Ukrainian'],['ur','Urdu'],['ug','Uyghur'],['uz','Uzbek'],['vi','Vietnamese'],['cy','Welsh'],
  ['xh','Xhosa'],['yi','Yiddish'],['yo','Yoruba'],['zu','Zulu'],
];

function getCurrentTranslateLang() {
  const m = document.cookie.match(/googtrans=\/en\/([a-zA-Z-]+)/);
  if (!m || m[1] === 'en') return 'en';
  return m[1];
}

// The actual translation engine, separate from TranslateWidget's visible
// picker UI — always mounted (including on minimal-header pages like entity
// landing pages) so a googtrans cookie set anywhere actually takes effect,
// not just on pages that also show the language-picker button.
function GoogleTranslateEngine() {
  useEffect(() => {
    if (window.google?.translate?.TranslateElement) return;
    if (document.getElementById('google-translate-script')) return;

    window.googleTranslateElementInit = () => {
      // eslint-disable-next-line no-undef
      new window.google.translate.TranslateElement({
        pageLanguage: 'en',
        layout: window.google.translate.TranslateElement.InlineLayout.SIMPLE,
        autoDisplay: false,
      }, 'google_translate_element');
    };
    const script = document.createElement('script');
    script.id = 'google-translate-script';
    script.src = 'https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
    script.async = true;
    document.body.appendChild(script);
  }, []);

  return (
    <>
      <style>{`
        .goog-te-banner-frame { display: none !important; }
        body { top: 0 !important; }
        #google_translate_element { position: absolute; width: 1px; height: 1px; overflow: hidden; opacity: 0; pointer-events: none; }
      `}</style>
      <div id="google_translate_element" />
    </>
  );
}

function TranslateWidget() {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const currentCode = getCurrentTranslateLang();
  const currentName = GOOGLE_TRANSLATE_LANGUAGES.find(l => l[0] === currentCode)?.[1] || 'English';
  const boxRef = React.useRef();

  // Script loading + the hidden #google_translate_element mount point now
  // live in GoogleTranslateEngine (always rendered by StoreLayout, even on
  // minimal-header pages) — this widget only needs the visible picker UI.

  useEffect(() => {
    const onClickOutside = e => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const pick = (code) => {
    const domain = window.location.hostname;
    // Two cookie writes: with and without an explicit domain — Google's own
    // widget is inconsistent about which one it reads back, so cover both.
    document.cookie = `googtrans=/en/${code}; path=/`;
    document.cookie = `googtrans=/en/${code}; path=/; domain=${domain}`;
    window.location.reload();
  };

  const filtered = GOOGLE_TRANSLATE_LANGUAGES.filter(l => l[1].toLowerCase().includes(search.toLowerCase()));

  return (
    <div ref={boxRef} style={{ position: 'relative' }}>
      <button type="button" onClick={() => setOpen(o => !o)} className="notranslate" style={{
        display: 'flex', alignItems: 'center', gap: 6, padding: '7px 12px', borderRadius: 20,
        border: '1px solid #e5e5e5', background: '#fff', cursor: 'pointer', fontSize: 13, color: '#333',
      }}>
        <i className="ti ti-language" style={{ fontSize: 15, color: 'var(--sf-accent)' }} />
        {currentName}
        <i className="ti ti-chevron-down" style={{ fontSize: 12 }} />
      </button>
      {open && (
        <div className="notranslate" style={{
          position: 'absolute', top: '100%', right: 0, marginTop: 6, background: '#fff',
          border: '1px solid #eee', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          width: 240, maxHeight: 360, overflow: 'hidden', zIndex: 200, display: 'flex', flexDirection: 'column',
        }}>
          <input autoFocus value={search} onChange={e => setSearch(e.target.value)} placeholder="Search language…"
            style={{ margin: 8, padding: '7px 10px', border: '1px solid #eee', borderRadius: 8, fontSize: 13 }} />
          <div style={{ overflowY: 'auto' }}>
            <div onClick={() => pick('en')} style={{ padding: '9px 14px', fontSize: 13.5, cursor: 'pointer', fontWeight: currentCode === 'en' ? 700 : 400 }}
              onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              English (original)
            </div>
            {filtered.map(([code, name]) => (
              <div key={code} onClick={() => pick(code)} style={{ padding: '9px 14px', fontSize: 13.5, cursor: 'pointer', fontWeight: currentCode === code ? 700 : 400 }}
                onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                {name}
              </div>
            ))}
            {filtered.length === 0 && <div style={{ padding: 14, fontSize: 12.5, color: '#999' }}>No match</div>}
          </div>
        </div>
      )}
    </div>
  );
}

// Follow-us icon row — reads the admin-editable social.<platform> /
// social.<platform>.active pairs (see Social Media Links in the admin panel)
// and shows only the platforms an admin has both filled in and switched on.
const SOCIAL_ICON = {
  facebook: 'ti-brand-facebook', instagram: 'ti-brand-instagram', twitter: 'ti-brand-x',
  linkedin: 'ti-brand-linkedin', youtube: 'ti-brand-youtube', pinterest: 'ti-brand-pinterest',
  tiktok: 'ti-brand-tiktok', whatsapp: 'ti-brand-whatsapp',
};
export function SocialIcons({ c, size = 32 }) {
  const active = Object.keys(SOCIAL_ICON).filter(p => c[`social.${p}.active`] === '1' && c[`social.${p}`]);
  if (!active.length) return null;
  return (
    <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
      {active.map(p => (
        <a key={p} href={c[`social.${p}`]} target="_blank" rel="noopener noreferrer" title={p} className="sf-social-icon"
          style={{
            width: size, height: size, borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: Math.round(size * 0.47), textDecoration: 'none',
          }}>
          <i className={`ti ${SOCIAL_ICON[p]}`} />
        </a>
      ))}
    </div>
  );
}

// Official Google-hosted "GET IT ON Google Play" badge (Google's own CDN,
// standard practice per their brand guidelines) — reused on the Mobile App
// page too, both pointing at the same admin-editable Play Store link.
export function GooglePlayBadge({ c, height = 46 }) {
  const url = c['mobileapp.google_play_url'] || 'https://play.google.com/store/apps/details?id=com.plexussquare.dcprakaasheyewr';
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-block' }}>
      <img src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png" alt="Get it on Google Play" loading="lazy" style={{ height, width: 'auto', display: 'block' }} />
    </a>
  );
}

// Real logo files for the well-known shipping/payment names — matched
// case-insensitively against the admin's comma list. Anything not in this
// map (e.g. NEFT, SWIFT — neither has a standalone brand mark) falls back
// to a plain text badge instead of a guessed/fabricated logo.
const BADGE_LOGOS = {
  dhl: '/product-images/_site/badges/dhl-color.svg',
  fedex: '/product-images/_site/badges/fedex-color.svg',
  visa: '/product-images/_site/badges/visa-color.svg',
  mastercard: '/product-images/_site/badges/mastercard-color.svg',
  maestro: '/product-images/_site/badges/maestro.svg',
  'apple pay': '/product-images/_site/badges/applepay-color.svg',
  rupay: '/product-images/_site/badges/rupay.svg',
};

// Footer bottom bar — rights-reserved / shipping partners / payment methods,
// all in one row (replaces the old plain centered copyright line). Names
// are admin-editable comma lists (Website Editor > Footer Bottom Bar).
function FooterBottomBar({ c }) {
  const shipping = (c['footer.shipping_partners'] || '').split(',').map(s => s.trim()).filter(Boolean);
  const payments = (c['footer.payment_methods'] || '').split(',').map(s => s.trim()).filter(Boolean);
  return (
    <div style={{ background: 'var(--sf-accent, #85cde9)', color: '#191919', marginTop: 30, padding: '14px 0' }}>
      <div style={{ ...wrap(), flexWrap: 'wrap', rowGap: 10, fontSize: 12, fontWeight: 600 }}>
        <div style={{ fontSize: 12.5 }}>© {new Date().getFullYear()} {c['footer.copyright_name'] || 'Prakaash Eyewear'}. All Rights Reserved.</div>
        {shipping.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <span>{c['footer.shipping_label'] || 'We ship with:'}</span>
            {shipping.map(s => <BadgeItem key={s} name={s} />)}
          </div>
        )}
        {payments.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <span>{c['footer.payment_label'] || 'Payment options:'}</span>
            {payments.map(p => <BadgeItem key={p} name={p} />)}
          </div>
        )}
      </div>
    </div>
  );
}

// All badges — text (GPay, NEFT, SWIFT) and logo image alike — share this
// exact box size so the whole "We ship with / Payment options" row stays
// compact and fits on one line instead of wrapping.
function BadgeItem({ name }) {
  const logo = BADGE_LOGOS[name.trim().toLowerCase()];
  if (logo) return (
    <span style={{ ...footerBadge, justifyContent: 'center' }}>
      <img src={logo} alt={name} title={name} loading="lazy" style={{ height: 14, width: 'auto', maxWidth: 40, display: 'block', objectFit: 'contain' }} />
    </span>
  );
  return <span style={footerBadge}>{name}</span>;
}

// Reused on the Cart page so "accepted payment methods" stays consistent
// with whatever's actually configured in Website Editor > Footer Bottom Bar,
// instead of a separately hardcoded icon set.
export function PaymentIcons({ names }) {
  const list = (names || '').split(',').map(s => s.trim()).filter(Boolean);
  if (!list.length) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
      {list.map(n => <BadgeItem key={n} name={n} />)}
    </div>
  );
}
const footerBadge = { background: '#fff', color: 'var(--sf-primary, #191919)', padding: '0 9px', borderRadius: 4, fontSize: 11, fontWeight: 700, letterSpacing: 0.3, height: 22, boxSizing: 'border-box', display: 'flex', alignItems: 'center' };

function FooterCol({ title, items }) {
  return (
    <div style={{ flex: '1 1 160px' }}>
      <div style={ftitle}>{title}</div>
      {(items || []).map(i => (
        <Link key={i.label} to={resolveLink(i.url)} style={{ display: 'block', color: '#777', fontSize: 12.5, textDecoration: 'none', padding: '3px 0' }}>{i.label}</Link>
      ))}
    </div>
  );
}

const wrap = () => ({ maxWidth: 1200, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 24px' });
const topLink = { color: '#fff', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4 };
const iconLink = { color: '#191919', textDecoration: 'none', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, fontSize: 11 };
const navLink = { padding: '13px 16px', fontSize: 13, fontWeight: 500, color: '#333', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer' };
const mobileNavLink = { display: 'block', padding: '13px 12px', fontSize: 14.5, fontWeight: 500, color: '#222', textDecoration: 'none', borderBottom: '1px solid #f2f2f2' };
const dropdown = { position: 'absolute', top: '100%', left: 0, background: '#fff', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', borderRadius: 8, padding: 8, minWidth: 240, zIndex: 100 };
const dropItem = { display: 'flex', justifyContent: 'space-between', padding: '8px 12px', fontSize: 13, color: '#333', textDecoration: 'none', borderRadius: 6 };
const dropColTitle = { fontSize: 11.5, fontWeight: 700, color: '#191919', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 10 };
const ftitle = { color: '#191919', fontSize: 13, fontWeight: 600, marginBottom: 12, textTransform: 'uppercase', letterSpacing: 0.5 };
export { IMG };
